import { FALLBACK } from "../../i18n/locales";
import { i18n } from "../../i18n";

export interface Country {
  code: string; // ISO 3166-1 alpha-2
  /** Display name in the add-in's language. */
  name: string;
  dialCode: string; // E.164 country calling code
}

interface CountryData {
  code: string;
  englishName: string;
  dialCode: string;
}

// Full country list, for phone-number normalization (REQ-GEN-006, REQ-NFR-005).
// The English names are only a fallback: countryList() replaces them with
// Intl.DisplayNames output, so there are no translations to maintain here.
const allCountries: CountryData[] = [
  { code: "AF", englishName: "Afghanistan", dialCode: "+93" },
  { code: "AL", englishName: "Albania", dialCode: "+355" },
  { code: "DZ", englishName: "Algeria", dialCode: "+213" },
  { code: "AD", englishName: "Andorra", dialCode: "+376" },
  { code: "AO", englishName: "Angola", dialCode: "+244" },
  { code: "AG", englishName: "Antigua and Barbuda", dialCode: "+1268" },
  { code: "AR", englishName: "Argentina", dialCode: "+54" },
  { code: "AM", englishName: "Armenia", dialCode: "+374" },
  { code: "AU", englishName: "Australia", dialCode: "+61" },
  { code: "AT", englishName: "Austria", dialCode: "+43" },
  { code: "AZ", englishName: "Azerbaijan", dialCode: "+994" },
  { code: "BS", englishName: "Bahamas", dialCode: "+1242" },
  { code: "BH", englishName: "Bahrain", dialCode: "+973" },
  { code: "BD", englishName: "Bangladesh", dialCode: "+880" },
  { code: "BB", englishName: "Barbados", dialCode: "+1246" },
  { code: "BY", englishName: "Belarus", dialCode: "+375" },
  { code: "BE", englishName: "Belgium", dialCode: "+32" },
  { code: "BZ", englishName: "Belize", dialCode: "+501" },
  { code: "BJ", englishName: "Benin", dialCode: "+229" },
  { code: "BT", englishName: "Bhutan", dialCode: "+975" },
  { code: "BO", englishName: "Bolivia", dialCode: "+591" },
  { code: "BA", englishName: "Bosnia and Herzegovina", dialCode: "+387" },
  { code: "BW", englishName: "Botswana", dialCode: "+267" },
  { code: "BR", englishName: "Brazil", dialCode: "+55" },
  { code: "BN", englishName: "Brunei", dialCode: "+673" },
  { code: "BG", englishName: "Bulgaria", dialCode: "+359" },
  { code: "BF", englishName: "Burkina Faso", dialCode: "+226" },
  { code: "BI", englishName: "Burundi", dialCode: "+257" },
  { code: "CV", englishName: "Cabo Verde", dialCode: "+238" },
  { code: "KH", englishName: "Cambodia", dialCode: "+855" },
  { code: "CM", englishName: "Cameroon", dialCode: "+237" },
  { code: "CA", englishName: "Canada", dialCode: "+1" },
  { code: "CF", englishName: "Central African Republic", dialCode: "+236" },
  { code: "TD", englishName: "Chad", dialCode: "+235" },
  { code: "CL", englishName: "Chile", dialCode: "+56" },
  { code: "CN", englishName: "China", dialCode: "+86" },
  { code: "CO", englishName: "Colombia", dialCode: "+57" },
  { code: "KM", englishName: "Comoros", dialCode: "+269" },
  { code: "CG", englishName: "Congo (Republic)", dialCode: "+242" },
  { code: "CD", englishName: "Congo (DRC)", dialCode: "+243" },
  { code: "CR", englishName: "Costa Rica", dialCode: "+506" },
  { code: "CI", englishName: "Côte d'Ivoire", dialCode: "+225" },
  { code: "HR", englishName: "Croatia", dialCode: "+385" },
  { code: "CU", englishName: "Cuba", dialCode: "+53" },
  { code: "CY", englishName: "Cyprus", dialCode: "+357" },
  { code: "CZ", englishName: "Czechia", dialCode: "+420" },
  { code: "DK", englishName: "Denmark", dialCode: "+45" },
  { code: "DJ", englishName: "Djibouti", dialCode: "+253" },
  { code: "DM", englishName: "Dominica", dialCode: "+1767" },
  { code: "DO", englishName: "Dominican Republic", dialCode: "+1809" },
  { code: "EC", englishName: "Ecuador", dialCode: "+593" },
  { code: "EG", englishName: "Egypt", dialCode: "+20" },
  { code: "SV", englishName: "El Salvador", dialCode: "+503" },
  { code: "GQ", englishName: "Equatorial Guinea", dialCode: "+240" },
  { code: "ER", englishName: "Eritrea", dialCode: "+291" },
  { code: "EE", englishName: "Estonia", dialCode: "+372" },
  { code: "SZ", englishName: "Eswatini", dialCode: "+268" },
  { code: "ET", englishName: "Ethiopia", dialCode: "+251" },
  { code: "FJ", englishName: "Fiji", dialCode: "+679" },
  { code: "FI", englishName: "Finland", dialCode: "+358" },
  { code: "FR", englishName: "France", dialCode: "+33" },
  { code: "GA", englishName: "Gabon", dialCode: "+241" },
  { code: "GM", englishName: "Gambia", dialCode: "+220" },
  { code: "GE", englishName: "Georgia", dialCode: "+995" },
  { code: "DE", englishName: "Germany", dialCode: "+49" },
  { code: "GH", englishName: "Ghana", dialCode: "+233" },
  { code: "GR", englishName: "Greece", dialCode: "+30" },
  { code: "GD", englishName: "Grenada", dialCode: "+1473" },
  { code: "GT", englishName: "Guatemala", dialCode: "+502" },
  { code: "GN", englishName: "Guinea", dialCode: "+224" },
  { code: "GW", englishName: "Guinea-Bissau", dialCode: "+245" },
  { code: "GY", englishName: "Guyana", dialCode: "+592" },
  { code: "HT", englishName: "Haiti", dialCode: "+509" },
  { code: "HN", englishName: "Honduras", dialCode: "+504" },
  { code: "HK", englishName: "Hong Kong", dialCode: "+852" },
  { code: "HU", englishName: "Hungary", dialCode: "+36" },
  { code: "IS", englishName: "Iceland", dialCode: "+354" },
  { code: "IN", englishName: "India", dialCode: "+91" },
  { code: "ID", englishName: "Indonesia", dialCode: "+62" },
  { code: "IR", englishName: "Iran", dialCode: "+98" },
  { code: "IQ", englishName: "Iraq", dialCode: "+964" },
  { code: "IE", englishName: "Ireland", dialCode: "+353" },
  { code: "IL", englishName: "Israel", dialCode: "+972" },
  { code: "IT", englishName: "Italy", dialCode: "+39" },
  { code: "JM", englishName: "Jamaica", dialCode: "+1876" },
  { code: "JP", englishName: "Japan", dialCode: "+81" },
  { code: "JO", englishName: "Jordan", dialCode: "+962" },
  { code: "KZ", englishName: "Kazakhstan", dialCode: "+7" },
  { code: "KE", englishName: "Kenya", dialCode: "+254" },
  { code: "KI", englishName: "Kiribati", dialCode: "+686" },
  { code: "KW", englishName: "Kuwait", dialCode: "+965" },
  { code: "KG", englishName: "Kyrgyzstan", dialCode: "+996" },
  { code: "LA", englishName: "Laos", dialCode: "+856" },
  { code: "LV", englishName: "Latvia", dialCode: "+371" },
  { code: "LB", englishName: "Lebanon", dialCode: "+961" },
  { code: "LS", englishName: "Lesotho", dialCode: "+266" },
  { code: "LR", englishName: "Liberia", dialCode: "+231" },
  { code: "LY", englishName: "Libya", dialCode: "+218" },
  { code: "LI", englishName: "Liechtenstein", dialCode: "+423" },
  { code: "LT", englishName: "Lithuania", dialCode: "+370" },
  { code: "LU", englishName: "Luxembourg", dialCode: "+352" },
  { code: "MO", englishName: "Macau", dialCode: "+853" },
  { code: "MG", englishName: "Madagascar", dialCode: "+261" },
  { code: "MW", englishName: "Malawi", dialCode: "+265" },
  { code: "MY", englishName: "Malaysia", dialCode: "+60" },
  { code: "MV", englishName: "Maldives", dialCode: "+960" },
  { code: "ML", englishName: "Mali", dialCode: "+223" },
  { code: "MT", englishName: "Malta", dialCode: "+356" },
  { code: "MH", englishName: "Marshall Islands", dialCode: "+692" },
  { code: "MR", englishName: "Mauritania", dialCode: "+222" },
  { code: "MU", englishName: "Mauritius", dialCode: "+230" },
  { code: "MX", englishName: "Mexico", dialCode: "+52" },
  { code: "FM", englishName: "Micronesia", dialCode: "+691" },
  { code: "MD", englishName: "Moldova", dialCode: "+373" },
  { code: "MC", englishName: "Monaco", dialCode: "+377" },
  { code: "MN", englishName: "Mongolia", dialCode: "+976" },
  { code: "ME", englishName: "Montenegro", dialCode: "+382" },
  { code: "MA", englishName: "Morocco", dialCode: "+212" },
  { code: "MZ", englishName: "Mozambique", dialCode: "+258" },
  { code: "MM", englishName: "Myanmar", dialCode: "+95" },
  { code: "NA", englishName: "Namibia", dialCode: "+264" },
  { code: "NR", englishName: "Nauru", dialCode: "+674" },
  { code: "NP", englishName: "Nepal", dialCode: "+977" },
  { code: "NL", englishName: "Netherlands", dialCode: "+31" },
  { code: "NZ", englishName: "New Zealand", dialCode: "+64" },
  { code: "NI", englishName: "Nicaragua", dialCode: "+505" },
  { code: "NE", englishName: "Niger", dialCode: "+227" },
  { code: "NG", englishName: "Nigeria", dialCode: "+234" },
  { code: "KP", englishName: "North Korea", dialCode: "+850" },
  { code: "MK", englishName: "North Macedonia", dialCode: "+389" },
  { code: "NO", englishName: "Norway", dialCode: "+47" },
  { code: "OM", englishName: "Oman", dialCode: "+968" },
  { code: "PK", englishName: "Pakistan", dialCode: "+92" },
  { code: "PW", englishName: "Palau", dialCode: "+680" },
  { code: "PS", englishName: "Palestine", dialCode: "+970" },
  { code: "PA", englishName: "Panama", dialCode: "+507" },
  { code: "PG", englishName: "Papua New Guinea", dialCode: "+675" },
  { code: "PY", englishName: "Paraguay", dialCode: "+595" },
  { code: "PE", englishName: "Peru", dialCode: "+51" },
  { code: "PH", englishName: "Philippines", dialCode: "+63" },
  { code: "PL", englishName: "Poland", dialCode: "+48" },
  { code: "PT", englishName: "Portugal", dialCode: "+351" },
  { code: "QA", englishName: "Qatar", dialCode: "+974" },
  { code: "RO", englishName: "Romania", dialCode: "+40" },
  { code: "RU", englishName: "Russia", dialCode: "+7" },
  { code: "RW", englishName: "Rwanda", dialCode: "+250" },
  { code: "KN", englishName: "Saint Kitts and Nevis", dialCode: "+1869" },
  { code: "LC", englishName: "Saint Lucia", dialCode: "+1758" },
  { code: "VC", englishName: "Saint Vincent and the Grenadines", dialCode: "+1784" },
  { code: "WS", englishName: "Samoa", dialCode: "+685" },
  { code: "SM", englishName: "San Marino", dialCode: "+378" },
  { code: "ST", englishName: "Sao Tome and Principe", dialCode: "+239" },
  { code: "SA", englishName: "Saudi Arabia", dialCode: "+966" },
  { code: "SN", englishName: "Senegal", dialCode: "+221" },
  { code: "RS", englishName: "Serbia", dialCode: "+381" },
  { code: "SC", englishName: "Seychelles", dialCode: "+248" },
  { code: "SL", englishName: "Sierra Leone", dialCode: "+232" },
  { code: "SG", englishName: "Singapore", dialCode: "+65" },
  { code: "SK", englishName: "Slovakia", dialCode: "+421" },
  { code: "SI", englishName: "Slovenia", dialCode: "+386" },
  { code: "SB", englishName: "Solomon Islands", dialCode: "+677" },
  { code: "SO", englishName: "Somalia", dialCode: "+252" },
  { code: "ZA", englishName: "South Africa", dialCode: "+27" },
  { code: "KR", englishName: "South Korea", dialCode: "+82" },
  { code: "SS", englishName: "South Sudan", dialCode: "+211" },
  { code: "ES", englishName: "Spain", dialCode: "+34" },
  { code: "LK", englishName: "Sri Lanka", dialCode: "+94" },
  { code: "SD", englishName: "Sudan", dialCode: "+249" },
  { code: "SR", englishName: "Suriname", dialCode: "+597" },
  { code: "SE", englishName: "Sweden", dialCode: "+46" },
  { code: "CH", englishName: "Switzerland", dialCode: "+41" },
  { code: "SY", englishName: "Syria", dialCode: "+963" },
  { code: "TW", englishName: "Taiwan", dialCode: "+886" },
  { code: "TJ", englishName: "Tajikistan", dialCode: "+992" },
  { code: "TZ", englishName: "Tanzania", dialCode: "+255" },
  { code: "TH", englishName: "Thailand", dialCode: "+66" },
  { code: "TL", englishName: "Timor-Leste", dialCode: "+670" },
  { code: "TG", englishName: "Togo", dialCode: "+228" },
  { code: "TO", englishName: "Tonga", dialCode: "+676" },
  { code: "TT", englishName: "Trinidad and Tobago", dialCode: "+1868" },
  { code: "TN", englishName: "Tunisia", dialCode: "+216" },
  { code: "TR", englishName: "Türkiye", dialCode: "+90" },
  { code: "TM", englishName: "Turkmenistan", dialCode: "+993" },
  { code: "TV", englishName: "Tuvalu", dialCode: "+688" },
  { code: "UG", englishName: "Uganda", dialCode: "+256" },
  { code: "UA", englishName: "Ukraine", dialCode: "+380" },
  { code: "AE", englishName: "United Arab Emirates", dialCode: "+971" },
  { code: "GB", englishName: "United Kingdom", dialCode: "+44" },
  { code: "US", englishName: "United States", dialCode: "+1" },
  { code: "UY", englishName: "Uruguay", dialCode: "+598" },
  { code: "UZ", englishName: "Uzbekistan", dialCode: "+998" },
  { code: "VU", englishName: "Vanuatu", dialCode: "+678" },
  { code: "VA", englishName: "Vatican City", dialCode: "+379" },
  { code: "VE", englishName: "Venezuela", dialCode: "+58" },
  { code: "VN", englishName: "Vietnam", dialCode: "+84" },
  { code: "YE", englishName: "Yemen", dialCode: "+967" },
  { code: "ZM", englishName: "Zambia", dialCode: "+260" },
  { code: "ZW", englishName: "Zimbabwe", dialCode: "+263" },
];

// Germany, Switzerland and Austria pinned to the top; the rest sorted per locale.
const PRIORITY = ["DE", "CH", "AT"];

/**
 * Country name in the add-in's language. Intl.DisplayNames is missing on older
 * Outlook desktop builds, so a failure falls back to English rather than blank.
 */
function localizedName(code: string, englishName: string, display?: Intl.DisplayNames): string {
  try {
    return display?.of(code) ?? englishName;
  } catch {
    return englishName;
  }
}

function makeDisplayNames(locale: string): Intl.DisplayNames | undefined {
  try {
    return new Intl.DisplayNames([locale], { type: "region" });
  } catch {
    return undefined;
  }
}

// Cached per locale: 198 Intl lookups plus a sort on every search keystroke
// would be wasteful, and the result only changes with the language.
let cache: { locale: string; list: Country[] } | undefined;

/** Every country, localised and sorted, with DE/CH/AT first. */
export function countryList(): Country[] {
  const locale = i18n.language || FALLBACK;
  if (cache?.locale === locale) {
    return cache.list;
  }

  const display = makeDisplayNames(locale);
  const named: Country[] = allCountries.map((c) => ({
    code: c.code,
    name: localizedName(c.code, c.englishName, display),
    dialCode: c.dialCode,
  }));

  // Alphabetical in this language: German puts Ägypten before Albanien.
  const collator = new Intl.Collator(locale);
  const rest = named
    .filter((c) => !PRIORITY.includes(c.code))
    .sort((a, b) => collator.compare(a.name, b.name));
  const list = [...PRIORITY.map((code) => named.find((c) => c.code === code)!), ...rest];

  cache = { locale, list };
  return list;
}

export function findCountry(code: string): Country | undefined {
  return countryList().find((c) => c.code === code);
}

/** The dial code (e.g. "+49") whose prefix best matches an E.164 number. */
export function dialCodeForPhone(e164: string): string | undefined {
  let best: string | undefined;
  for (const c of allCountries) {
    if (e164.startsWith(c.dialCode) && (best === undefined || c.dialCode.length > best.length)) {
      best = c.dialCode;
    }
  }
  return best;
}

/** Filter by country name, ISO code (e.g. "DE"), or dial code (e.g. "+49" / "49"). */
export function searchCountries(query: string): Country[] {
  const list = countryList();
  const q = query.trim().toLowerCase();
  if (!q) return list;

  // A numeric query ("49", "+94") is a dial-code search — match by prefix so
  // "49" gives +49 (not +249) and "+94" gives +94 (not +994).
  if (/^\+?\d/.test(q)) {
    const digits = q.replace(/[^\d]/g, "");
    return list.filter((c) => c.dialCode.replace(/[^\d]/g, "").startsWith(digits));
  }

  // Name or ISO code. The English name stays searchable too, so someone on a
  // German Outlook typing "Germany" still finds it.
  const english = new Map(allCountries.map((c) => [c.code, c.englishName.toLowerCase()]));
  return list.filter(
    (c) =>
      c.name.toLowerCase().includes(q) ||
      c.code.toLowerCase().includes(q) ||
      (english.get(c.code) ?? "").includes(q)
  );
}
